import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useCashflowTemplates } from '@/hooks/useCashflowTemplates';
import { GRAPH_ASSETS_QUERY_NEW } from '@/pages/PortfolioAnalysis/PortfolioGraphing/operations';
import { ASSETS_QUERY } from '@/pages/Valuations/AssetList/operations';
import { MODEL_TYPES } from '@/utils/cashflowConstants';
import { normalizePortfolioFilters } from '@/utils/portfolioUtils';
import Modal from '..';
// Feature flag: enable_consolidated_live_models
import { styles as portfolioModelStyles } from './stylesCLM';

const useStyles = makeStyles(() => ({
  modal: {
    backgroundColor: 'white',
    height: 'auto',
    width: '70%',
    maxWidth: '750px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
}));

const TemplateSelectionList = ({
  consolidatedLiveModelsAdmin,
  selectedTemplateId,
  setSelectedTemplateId,
  loadingTemplates,
  templates,
  styles,
}) => {
  if (!consolidatedLiveModelsAdmin) {
    return null;
  }

  return (
    <Box sx={styles.templateListScrollContainer} flexGrow={1}>
      <Box>
        <Typography variant='subtitle2' sx={styles.templateListTitle}>
          Available Templates
        </Typography>
        <Box
          onClick={() => setSelectedTemplateId(null)}
          sx={{
            ...styles.templateItem,
            ...(selectedTemplateId === null ? styles.templateItemSelected : {}),
          }}
          data-testid='template-item-none'
        >
          <Typography variant='subtitle1' fontWeight='bold'>
            None
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Do not use a saved template
          </Typography>
        </Box>
        {loadingTemplates ? (
          <Typography>Loading templates...</Typography>
        ) : templates?.length > 0 ? (
          templates.map(template => (
            <Box
              key={template.id}
              onClick={() => setSelectedTemplateId(template.id)}
              sx={{
                ...styles.templateItem,
                ...(selectedTemplateId === template.id ? styles.templateItemSelected : {}),
              }}
              data-testid={`template-item-${template.id}`}
            >
              <Box>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {template.name}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Created by{' '}
                  {template.createdBy
                    ? `${template.createdBy.firstName} ${template.createdBy.lastName}`
                    : 'Unknown'}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : ''}
                </Typography>
              </Box>
              <Chip
                label={template.isPublished ? 'Published' : 'Draft'}
                color={template.isPublished ? 'success' : 'default'}
                size='small'
                sx={{ ml: 2 }}
              />
            </Box>
          ))
        ) : (
          <Typography>No templates available.</Typography>
        )}
      </Box>
    </Box>
  );
};

export const PortfolioModel = ({ open, setOpen, onRequestReport, portfolios }) => {
  const { consolidatedLiveModelsAdmin } = useFeatureFlags();
  const [emailReport, setEmailReport] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [reportName, setReportName] = useState('');
  const modalStyle = useStyles();
  const theme = useTheme();
  const styles = portfolioModelStyles(theme, {
    showTemplateDropdown: consolidatedLiveModelsAdmin,
  });
  const reportNameId = 'portfolio-model-report-name';

  const adjustableInputs = [
    [
      'Commodity prices',
      'Discount rate & inflation',
      'Acquisitions & divestments',
      'Project timing',
    ],
    ['Capex & opex', 'Tax synergies', 'Overhead and G&A'],
  ];

  const portfolioSummaryHeaders = ['Companies', 'Assets', 'Countries', 'Reserves (MMboe)'];

  const getPortfolioSummaryData = () => {
    const summary = getPortfolioSummary();
    return [
      summary.companies,
      summary.assets,
      summary.countries,
      summary.reserves > 0 ? summary.reserves.toLocaleString() : 'N/A',
    ];
  };

  const { templates, loadingTemplates } = useCashflowTemplates(MODEL_TYPES.clm.typeName);

  const [getAssets, { data: assetsData, loading: loadingAssets }] = useLazyQuery(ASSETS_QUERY);

  // Get asset details for selected assets
  // biome-ignore lint/correctness/noUnusedVariables: Variable may be used in future features
  const [getAssetDetails, { data: assetDetailsData, loading: loadingAssetDetails }] =
    useLazyQuery(ASSETS_QUERY);

  // Instead of using a separate query, we'll get the data from the TreemapContext
  // This ensures we get the same data that the Treemap is using
  const {
    data: reservesData,
    // biome-ignore lint/correctness/noUnusedVariables: Variables may be used in future features
    loading: loadingReserves,
    // biome-ignore lint/correctness/noUnusedVariables: Variable may be used in future features
    error: reservesError,
  } = useQuery(GRAPH_ASSETS_QUERY_NEW, {
    variables: {
      aggregates: ['Company', 'Asset'], // Use the same aggregates as TreemapContext
      chartType: 'treemap',
      graphType: 'Reserves',
      graphTypeOptions: [],
      portfolios: normalizePortfolioFilters(portfolios), // Normalize portfolios like Portfolio component
    },
    skip: portfolios.length === 0, // Skip if no portfolios
  });

  const extractUniqueNamesFromPortfolios = (filterKey, propertyName = 'name') => {
    return [
      ...new Set(
        portfolios.flatMap(portfolio =>
          (portfolio.filters[filterKey] || []).map(item => item[propertyName]),
        ),
      ),
    ];
  };

  // Calculate real portfolio data
  const getPortfolioSummary = () => {
    const companyNames = extractUniqueNamesFromPortfolios('companies');
    const assetNames = extractUniqueNamesFromPortfolios('assets');

    // Extract countries from both countries filter and assets
    const countriesFromFilter = extractUniqueNamesFromPortfolios('countries', 'displayName');
    const countriesFromAssets = portfolios.flatMap(portfolio =>
      (portfolio.filters.assets || [])
        .map(asset => asset.country?.displayName || asset.country?.name)
        .filter(Boolean),
    );
    const allCountries = [...new Set([...countriesFromFilter, ...countriesFromAssets])];

    // Calculate total reserves from the selected assets
    let totalReserves = 0;

    // First try to get reserves from the Treemap data
    if (reservesData?.graphAssetsNew?.analytics?.[0]?.values) {
      const values = reservesData.graphAssetsNew.analytics[0].values;
      const labels = reservesData.graphAssetsNew.analytics[0].labels;

      // Get the selected asset names
      const selectedAssetNames = portfolios.flatMap(
        portfolio => portfolio.filters.assets?.map(asset => asset.name) || [],
      );

      if (selectedAssetNames.length > 0) {
        // If we have explicitly selected assets, find the values for those assets
        const selectedAssetName = selectedAssetNames[0];

        // Find indices where the label matches our selected asset
        const assetIndices = labels
          .map((label, index) => (label === selectedAssetName ? index : -1))
          .filter(index => index !== -1);

        if (assetIndices.length > 0) {
          // Use the first value for the selected asset
          const assetValue = values[assetIndices[0]];
          totalReserves = assetValue || 0;
        } else {
          totalReserves = values[0] || 0;
        }
      } else {
        // If no assets are explicitly selected, sum all values from the Treemap
        // This covers cases where we have countries or companies selected
        totalReserves = values.reduce((sum, value) => sum + (value || 0), 0);
      }
    } else if (assetDetailsData?.assetsConnection?.edges?.length > 0) {
      assetDetailsData.assetsConnection.edges.forEach(edge => {
        const asset = edge.node;

        // Try to get reserves from the detailed asset data
        if (asset.reserve?.formattedTotalRemainingReservesLiquidsMmbbl) {
          const liquids =
            parseFloat(asset.reserve.formattedTotalRemainingReservesLiquidsMmbbl) || 0;
          const gas = parseFloat(asset.reserve.formattedTotalRemainingReservesGasBcf) || 0;
          // Convert gas to MMboe (divide by 6)
          const assetReserves = liquids + gas / 6;
          totalReserves += assetReserves;
        } else if (asset.remainingReserves) {
          // Fallback to remainingReserves field
          const assetReserves = parseFloat(asset.remainingReserves) || 0;
          totalReserves += assetReserves;
        } else if (asset.reserve?.totalRemainingReservesLiquidsMmbbl) {
          // Try alternative field names
          const liquids = parseFloat(asset.reserve.totalRemainingReservesLiquidsMmbbl) || 0;
          const gas = parseFloat(asset.reserve.totalRemainingReservesGasBcf) || 0;
          const assetReserves = liquids + gas / 6;
          totalReserves += assetReserves;
        }
      });
    } else {
      // Fallback: calculate from selected assets (basic data)
      portfolios.forEach(portfolio => {
        if (portfolio.filters.assets && portfolio.filters.assets.length > 0) {
          portfolio.filters.assets.forEach(asset => {
            // Try to get reserves from the basic asset data
            if (asset.reserve?.formattedTotalRemainingReservesLiquidsMmbbl) {
              const liquids =
                parseFloat(asset.reserve.formattedTotalRemainingReservesLiquidsMmbbl) || 0;
              const gas = parseFloat(asset.reserve.formattedTotalRemainingReservesGasBcf) || 0;
              // Convert gas to MMboe (divide by 6)
              const assetReserves = liquids + gas / 6;
              totalReserves += assetReserves;
            } else if (asset.remainingReserves) {
              // Fallback to remainingReserves field
              const assetReserves = parseFloat(asset.remainingReserves) || 0;
              totalReserves += assetReserves;
            } else if (asset.reserve?.totalRemainingReservesLiquidsMmbbl) {
              // Try alternative field names
              const liquids = parseFloat(asset.reserve.totalRemainingReservesLiquidsMmbbl) || 0;
              const gas = parseFloat(asset.reserve.totalRemainingReservesGasBcf) || 0;
              const assetReserves = liquids + gas / 6;
              totalReserves += assetReserves;
            }
          });
        }
      });
    }

    // Use real assets count if we have companies selected but no explicit assets
    // OR if we have countries selected but no companies and no explicit assets
    let assetsCount = assetNames.length;
    if (
      ((companyNames.length > 0 && assetNames.length === 0) ||
        (companyNames.length === 0 && allCountries.length > 0 && assetNames.length === 0)) &&
      !loadingAssets &&
      assetsData
    ) {
      assetsCount = assetsData.assetsConnection?.totalCount || 0;
    }

    return {
      companies: companyNames.length,
      assets: assetsCount,
      countries: allCountries.length,
      reserves: totalReserves,
    };
  };

  // Get assets count when companies are selected
  useEffect(() => {
    const companyNames = extractUniqueNamesFromPortfolios('companies');
    const assetNames = extractUniqueNamesFromPortfolios('assets');
    const _countryNames = extractUniqueNamesFromPortfolios('countries');

    // Extract country ISO codes from portfolios
    const countryIsoCodes = portfolios
      .flatMap(portfolio => portfolio.filters.countries?.map(country => country.isoCode) || [])
      .filter(Boolean);

    // If we have companies but no explicit assets, fetch assets for those companies
    // OR if we have countries but no companies and no explicit assets, fetch assets for those countries
    if (
      (companyNames.length > 0 && assetNames.length === 0) ||
      (companyNames.length === 0 && countryIsoCodes.length > 0 && assetNames.length === 0)
    ) {
      const variables = {
        assetLegacyIds: [],
        assetIsoCodes: [],
        company: companyNames,
        country: countryIsoCodes, // Use ISO codes instead of names
        basin: [], // Note: singular, not plural
        developmentStatus: [],
        developmentType: [],
        shoreStatus: [],
        geologyEpochIds: [],
        hydrocarbonType: [],
        includeHistoricalParticipants: false,
        operator: false,
        nonOperator: false,
        opportunities: false,
        oilType: [],
        npvMin: null,
        npvMax: null,
        reservesMin: null,
        reservesMax: null,
        productionMin: null,
        productionMax: null,
        depthMin: null,
        depthMax: null,
        isWalkthrough: false,
        sortType: 'name',
        sortAscending: true,
        first: 1, // We only need the count
        after: null,
      };

      getAssets({ variables });
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy useEffect, will refactor later
  }, [portfolios, getAssets, extractUniqueNamesFromPortfolios]);

  // Get detailed asset data for selected assets
  useEffect(() => {
    const selectedAssetIds = portfolios
      .flatMap(portfolio => portfolio.filters.assets?.map(asset => asset.legacyId) || [])
      .filter(Boolean);

    if (selectedAssetIds.length > 0) {
      const variables = {
        assetLegacyIds: selectedAssetIds,
        assetIsoCodes: [],
        company: [],
        country: [],
        basin: [],
        developmentStatus: [],
        developmentType: [],
        shoreStatus: [],
        geologyEpochIds: [],
        hydrocarbonType: [],
        includeHistoricalParticipants: false,
        operator: false,
        nonOperator: false,
        opportunities: false,
        oilType: [],
        npvMin: null,
        npvMax: null,
        reservesMin: null,
        reservesMax: null,
        productionMin: null,
        productionMax: null,
        depthMin: null,
        depthMax: null,
        isWalkthrough: false,
        sortType: 'name',
        sortAscending: true,
        first: 100,
        after: null,
      };
      getAssetDetails({ variables });
    }
  }, [portfolios, getAssetDetails]);

  // Reserves data is now fetched directly with useQuery (same as TreemapContext)

  const _portfolioSummary = getPortfolioSummary();
  const companyNames = extractUniqueNamesFromPortfolios('companies');
  const _assetNames = extractUniqueNamesFromPortfolios('assets');
  // Build country names list from filters and assets
  const countriesFromFilter = extractUniqueNamesFromPortfolios('countries', 'displayName');
  const countriesFromAssets = portfolios
    .flatMap(portfolio => portfolio.filters.assets || [])
    .map(asset => asset.country?.displayName || asset.country?.name)
    .filter(Boolean);
  const countryNames = [...new Set([...countriesFromFilter, ...countriesFromAssets])];
  // biome-ignore lint/correctness/noUnusedVariables: Variable may be used in future features
  const assetUnitNames = extractUniqueNamesFromPortfolios('assetUnits', 'optionDisplayName');

  const clear = () => {
    setEmailReport(false);
    setSelectedTemplateId(null);
    setReportName('');
  };

  const handleClose = () => {
    clear();
    setOpen(false);
  };

  const handleRequestReport = () => {
    onRequestReport({
      modelType: MODEL_TYPES.clm.typeName,
      templateId: selectedTemplateId,
      writeFlag: false,
      emailReport,
      reportName,
    });
    handleClose();
  };

  return (
    <Modal
      handleClose={handleClose}
      open={open}
      title='PORTFOLIO MODEL'
      titleAlignedWithClose={true}
      styles={modalStyle}
    >
      <Box sx={styles.modalContentWrapper}>
        <Divider />

        <Box sx={styles.modalContent}>
          {/* Portfolio Summary */}
          <Box pt={2} pb={1}>
            <Typography variant='subtitle2' sx={styles.portfolioSummaryTitle}>
              Portfolio Summary:
            </Typography>
            <Box sx={styles.portfolioSummaryTable}>
              <Box sx={styles.portfolioSummaryHeader}>
                {portfolioSummaryHeaders.map((header, index) => (
                  <Box
                    key={header}
                    sx={
                      index < portfolioSummaryHeaders.length - 1
                        ? styles.portfolioSummaryHeaderCell
                        : styles.portfolioSummaryHeaderCellLast
                    }
                  >
                    <Typography variant='body2' sx={styles.portfolioSummaryHeaderText}>
                      {header}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={styles.portfolioSummaryData}>
                {getPortfolioSummaryData().map((value, index) => (
                  <Box
                    // biome-ignore lint/suspicious/noArrayIndexKey: Legacy code pattern, will refactor later
                    key={index}
                    sx={
                      index < portfolioSummaryHeaders.length - 1
                        ? styles.portfolioSummaryDataCell
                        : styles.portfolioSummaryDataCellLast
                    }
                  >
                    <Typography variant='h6' sx={styles.portfolioSummaryDataText}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Selected Filters */}
          <Box py={1.5}>
            <Typography variant='subtitle2' sx={styles.selectedFiltersTitle}>
              Selected Filters:
            </Typography>
            <Box mb={0.5}>
              <Typography variant='body2' sx={styles.selectedFiltersLabel}>
                Companies
              </Typography>
              <Typography variant='body2' sx={styles.selectedFiltersValue}>
                {companyNames.length > 0 ? companyNames.join(', ') : 'No companies selected'}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' sx={styles.selectedFiltersLabel}>
                Countries
              </Typography>
              <Typography variant='body2' sx={styles.selectedFiltersValue}>
                {countryNames.length > 0 ? countryNames.join(', ') : 'No countries selected'}
              </Typography>
            </Box>
          </Box>

          {/* Information Box */}
          <Box pb={1.5}>
            <Box sx={styles.infoBox}>
              <Box sx={styles.infoBoxContent}>
                <Box sx={styles.infoBoxIcon}>
                  <Typography sx={styles.infoBoxIconText}>i</Typography>
                </Box>
                <Typography variant='caption' sx={styles.infoBoxText}>
                  Using the download, dynamically model portfolio-level cash flows and key metrics
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Adjustable Inputs */}
          <Box pb={1.5}>
            <Typography variant='caption' sx={styles.adjustableInputsTitle}>
              Adjustable inputs in the Portfolio Model download include:
            </Typography>
            <Box sx={styles.adjustableInputsGrid}>
              {adjustableInputs.map((column, columnIndex) => (
                <Box
                  // biome-ignore lint/suspicious/noArrayIndexKey: Legacy code pattern, will refactor later
                  key={columnIndex}
                >
                  {column.map((item, itemIndex) => (
                    <Typography
                      // biome-ignore lint/suspicious/noArrayIndexKey: Legacy code pattern, will refactor later
                      key={itemIndex}
                      variant='caption'
                      sx={{
                        ...styles.adjustableInputsItem,
                        mb: itemIndex === column.length - 1 ? 0 : 0.2,
                      }}
                    >
                      <strong>• {item}</strong>
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Customize File Name */}
          <Box>
            <Typography sx={styles.customizeFileNameLabel}>Customize File Name</Typography>
            <TextField
              id={reportNameId}
              variant='outlined'
              size='small'
              value={reportName}
              onChange={e => setReportName(e.target.value)}
              sx={styles.customizeFileNameField}
            />
          </Box>

          {/* Available Templates */}
          <TemplateSelectionList
            consolidatedLiveModelsAdmin={consolidatedLiveModelsAdmin}
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={setSelectedTemplateId}
            loadingTemplates={loadingTemplates}
            templates={templates}
            styles={styles}
          />
        </Box>

        {/* Footer */}
        <div style={styles.spaceBetween}>
          <div style={styles.formControlRow}>
            <FormControlLabel
              control={
                <Switch checked={emailReport} onChange={() => setEmailReport(!emailReport)} />
              }
              label='Email Report'
            />
          </div>
          <div style={styles.formControlRow}>
            <Box sx={{ ...styles.buttonsContainer, ...styles.boxFlex }}>
              <Button
                onClick={() => handleRequestReport(true)}
                sx={{
                  ...styles.button,
                }}
                variant='contained'
              >
                Generate Model
              </Button>
            </Box>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
